(() => {
  if (window.WobSwatches?.getSwatchStyle) return;

  const handleize = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const humanizeSwatchLabel = (value) => {
    return String(value || '')
      .replace(/[-_]+/g, ' ')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const swatchKey = (value) => handleize(humanizeSwatchLabel(value));

  const config = window.WobSwatchConfig || {};
  const publishedSwatches = new Map();
  const themeVectors = new Map(
    Object.entries(config.vectors || {}).map(([handle, url]) => [
      handleize(handle),
      String(url || '').trim()
    ])
  );

  const getThemeVector = (key) => {
    if (themeVectors.has(key)) return themeVectors.get(key);
    if (key.includes('camo')) return themeVectors.get('camouflage') || '';
    if (key.includes('leopard') || key.includes('cheetah') || key.includes('animal-print')) {
      return themeVectors.get('leopard') || '';
    }
    if (key.includes('cow')) return themeVectors.get('cow-print') || '';
    if (key.includes('tortoise')) return themeVectors.get('tortoise') || '';
    if (key.includes('gingham')) return themeVectors.get('gingham') || '';
    if (key.includes('checkered') && key.includes('black')) {
      return themeVectors.get('checkered-black') || '';
    }
    if (key.includes('checkered')) return themeVectors.get('checkered-tan') || '';
    if (key.includes('floral')) return themeVectors.get('floral') || '';
    if (key.includes('rainbow')) return themeVectors.get('rainbow') || '';
    if (key.includes('multi')) return themeVectors.get('multi') || '';
    return '';
  };

  const safeColor = (value) => {
    const color = String(value || '').trim().toLowerCase();
    return /^#(?:[a-f0-9]{3,4}|[a-f0-9]{6}|[a-f0-9]{8})$/i.test(color) ? color : '';
  };

  const safeGradient = (value) => {
    const gradient = String(value || '').trim();
    let depth = 0;
    let balanced = true;
    for (const character of gradient) {
      if (character === '(') depth += 1;
      if (character === ')') depth -= 1;
      if (depth < 0) balanced = false;
    }
    if (
      !gradient
      || gradient.length > 1000
      || !/^(?:(?:repeating-)?(?:linear|radial|conic)-gradient)\(/i.test(gradient)
      || /[;{}<>"']/.test(gradient)
      || /url\(/i.test(gradient)
      || !balanced
      || depth !== 0
    ) return '';
    if (window.CSS?.supports && !window.CSS.supports('background-image', gradient)) return '';
    return gradient;
  };

  const normalizePublishedSwatch = (entry) => {
    if (!entry || entry.active === false) return null;
    const color = safeColor(entry.color);
    const gradient = safeGradient(entry.gradient);
    if (!color && !gradient) return null;

    return {
      name: humanizeSwatchLabel(entry.name),
      color: color || '#d9d9d9',
      image: gradient
    };
  };

  const getPublishedSwatch = (value) => {
    return publishedSwatches.get(swatchKey(value))
      || publishedSwatches.get(handleize(value))
      || null;
  };

  const getSwatchLabel = (value) => {
    return getPublishedSwatch(value)?.name || humanizeSwatchLabel(value);
  };

  const swatchColorMap = {
    black: '#111111',
    'vintage-black': '#1d1b19',
    white: '#ffffff',
    'off-white': '#f8f4ea',
    ivory: '#fffff0',
    ecru: '#fffff0',
    pearl: '#fffff0',
    cream: '#f4ecd8',
    oatmeal: '#f4ecd8',
    bone: '#f4ecd8',
    'light-beige': '#f4ecd8',
    'heather-oatmeal': '#f4ecd8',
    vanilla: '#f3e6c8',
    ballet: '#ead2d8',
    champagne: '#e8d6b3',
    frost: '#eef1f3',
    cloud: '#eef1f3',
    clear: '#f7f7f7',
    beige: '#d8c4aa',
    natural: '#d8c4aa',
    sand: '#d8c4aa',
    nude: '#d9b8a3',
    mushroom: '#b7aea2',
    shell: '#f3d1c4',
    tan: '#b78355',
    camel: '#b78355',
    khaki: '#b78355',
    brown: '#6f4e37',
    chocolate: '#6f4e37',
    mocha: '#6f4e37',
    espresso: '#6f4e37',
    coffee: '#6f4e37',
    cocoa: '#6f4e37',
    pecan: '#6f4e37',
    truffle: '#6f4e37',
    cinnamon: '#9a4f2f',
    mahogany: '#5a261b',
    caramel: '#b8753b',
    toffee: '#b8753b',
    cognac: '#9f5a2c',
    latte: '#c39a74',
    toast: '#b9875b',
    chestnut: '#7b4a2f',
    burlwood: '#7b4a2f',
    taupe: '#8b7c6e',
    stone: '#8b7c6e',
    'light-taupe': '#8b7c6e',
    gray: '#9a9a9a',
    grey: '#9a9a9a',
    'ash-gray': '#9a9a9a',
    'ash-grey': '#9a9a9a',
    'gray-melange': '#9a9a9a',
    'grey-melange': '#9a9a9a',
    'heather-gray': '#9a9a9a',
    'heather-grey': '#9a9a9a',
    charcoal: '#444444',
    'dark-charcoal': '#2f3336',
    navy: '#18253f',
    midnight: '#111c32',
    indigo: '#243f87',
    blue: '#3269b1',
    azure: '#2f80c9',
    cerulean: '#2477b8',
    cobalt: '#1f4fb8',
    cyan: '#36bfd0',
    coastal: '#7daebb',
    'dusty-coast': '#7daebb',
    'light-blue': '#a8cce8',
    'sky-blue': '#a8cce8',
    'baby-blue': '#a8cce8',
    'misty-blue': '#a8cce8',
    periwinkle: '#a8cce8',
    'dusty-periwinkle': '#a8cce8',
    chambray: '#8faec4',
    'ocean-blue': '#517796',
    'slate-blue': '#517796',
    denim: '#557aa2',
    'washed-black': '#1f2428',
    'brown-wash': '#7a5a45',
    'deep-blue': '#315d7e',
    'steel-blue': '#315d7e',
    phantom: '#244866',
    ambush: '#244866',
    eclipse: '#244866',
    fearless: '#244866',
    hustler: '#244866',
    mayhem: '#244866',
    mystique: '#244866',
    'no-limits': '#244866',
    riot: '#244866',
    shadow: '#244866',
    untamed: '#244866',
    outlaw: '#8fb0c8',
    breakdown: '#8fb0c8',
    'laid-back': '#8fb0c8',
    lowkey: '#8fb0c8',
    'vintage-light-wash': '#8fb0c8',
    taffy: '#d9e5df',
    authentic: '#557aa2',
    blissful: '#557aa2',
    carefree: '#557aa2',
    cosmic: '#557aa2',
    curveball: '#557aa2',
    'free-spirit': '#557aa2',
    'good-vibes': '#557aa2',
    icon: '#557aa2',
    iconic: '#557aa2',
    legacy: '#557aa2',
    legend: '#557aa2',
    muse: '#557aa2',
    oasis: '#557aa2',
    overheard: '#557aa2',
    relentless: '#557aa2',
    serene: '#557aa2',
    stardust: '#557aa2',
    thrill: '#557aa2',
    treasure: '#557aa2',
    unbothered: '#557aa2',
    unfazed: '#557aa2',
    'urban-drift': '#557aa2',
    wild: '#557aa2',
    green: '#4f7a55',
    forest: '#234d35',
    'deep-forest': '#183f2a',
    pine: '#1f4a34',
    'dusty-pine': '#4f6d5a',
    spruce: '#2f5a4f',
    teal: '#277c78',
    'deep-teal': '#277c78',
    aqua: '#56b6b1',
    turquoise: '#56b6b1',
    seafoam: '#56b6b1',
    'soft-turquoise': '#56b6b1',
    olive: '#708238',
    'dark-olive': '#708238',
    'light-olive': '#708238',
    'powder-olive': '#708238',
    sage: '#9caf88',
    'dusty-sage': '#9caf88',
    mint: '#b9e3c6',
    'soft-mint': '#b9e3c6',
    honeydew: '#b9e3c6',
    apple: '#78a641',
    'apple-green': '#78a641',
    emerald: '#1f7a4f',
    matcha: '#8aa45d',
    pistachio: '#a9b86f',
    avocado: '#7f9d45',
    fern: '#55764b',
    lime: '#b7cf4a',
    willow: '#c4cc86',
    red: '#b3261e',
    cherry: '#b3261e',
    poppy: '#b3261e',
    scarlet: '#b3261e',
    heatwave: '#b3261e',
    tomato: '#d8452f',
    cranberry: '#9f1f3a',
    crimson: '#9f1f3a',
    ruby: '#9f1f3a',
    burgundy: '#7b1e2b',
    wine: '#7b1e2b',
    sangria: '#7b1e2b',
    'spiced-sangria': '#7b1e2b',
    pink: '#e8a5b5',
    'baby-pink': '#e8a5b5',
    'candy-pink': '#e8a5b5',
    'pale-pink': '#e8a5b5',
    'punch-pink': '#e8a5b5',
    punch: '#e8a5b5',
    bubblegum: '#e8a5b5',
    petal: '#e8a5b5',
    blush: '#f3c5c5',
    'dusty-rose': '#f3c5c5',
    blossom: '#f0a7ba',
    flamingo: '#e85c82',
    strawberry: '#e85c82',
    watermelon: '#e85c82',
    hibiscus: '#e85c82',
    raspberry: '#e85c82',
    purple: '#7759a6',
    plum: '#673147',
    'dusty-plum': '#673147',
    lavender: '#b9a3d7',
    lilac: '#b9a3d7',
    orchid: '#b9a3d7',
    'ombre-violet': '#8554bd',
    'pale-mauve': '#dec7d2',
    palemauve: '#dec7d2',
    mauve: '#ad7f91',
    boysenberry: '#6f315d',
    magenta: '#c13c8a',
    fuchsia: '#c13c8a',
    'deep-magenta': '#9d1b68',
    orange: '#d9793d',
    'burnt-orange': '#d9793d',
    sorbet: '#d9793d',
    pumpkin: '#d9793d',
    tangerine: '#f47a2a',
    coral: '#e47f72',
    'coral-pink': '#e47f72',
    rust: '#b65c35',
    sienna: '#b65c35',
    'burnt-sienna': '#b65c35',
    auburn: '#b65c35',
    spice: '#b65c35',
    brick: '#b65c35',
    terracotta: '#b85f45',
    'soft-peach': '#f2d8d3',
    softpeach: '#f2d8d3',
    peach: '#f2a378',
    melon: '#f2a378',
    grapefruit: '#ee6f62',
    apricot: '#e7a06e',
    papaya: '#e98b54',
    copper: '#e98b54',
    yellow: '#e5c64a',
    lemon: '#e5c64a',
    limoncello: '#e5c64a',
    butter: '#e5c64a',
    daisy: '#e5c64a',
    banana: '#e5c64a',
    citrus: '#e5c64a',
    sunflower: '#f2b928',
    sunshine: '#f2b928',
    daffodil: '#f2b928',
    dandelion: '#f2b928',
    amber: '#c9822f',
    gold: '#c7a349',
    mama: '#e9aebc',
    silver: '#c8c8c8'
  };

  const swatchImageMap = {
    multi: 'linear-gradient(135deg, #111 0 20%, #fff 20% 40%, #b3261e 40% 60%, #3269b1 60% 80%, #e5c64a 80% 100%)',
    multicolor: 'linear-gradient(135deg, #111 0 20%, #fff 20% 40%, #b3261e 40% 60%, #3269b1 60% 80%, #e5c64a 80% 100%)',
    'multi-color': 'linear-gradient(135deg, #111 0 20%, #fff 20% 40%, #b3261e 40% 60%, #3269b1 60% 80%, #e5c64a 80% 100%)',
    rainbow: 'linear-gradient(135deg, #e63946 0 16%, #ff9f1c 16% 32%, #f6d84a 32% 48%, #2f9e44 48% 64%, #1971c2 64% 80%, #845ec2 80% 100%)',
    'cotton-candy': 'linear-gradient(135deg, #f6b9d0 0%, #f9d8e6 48%, #a8d9f4 100%)',
    clear: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(229,234,237,0.9) 48%, rgba(255,255,255,0.65) 100%)',
    mama: 'linear-gradient(135deg, #f5c8d3 0%, #f7efe3 52%, #c89b7b 100%)',
    'ombre-violet': 'linear-gradient(135deg, #f0e4ff 0%, #b87ad9 45%, #4d2678 100%)',
    'black-wash': 'linear-gradient(135deg, #111820 0%, #2b333b 48%, #07090b 100%)',
    'gray-wash': 'linear-gradient(135deg, #626a70 0%, #aab0b4 50%, #50575d 100%)',
    'grey-wash': 'linear-gradient(135deg, #626a70 0%, #aab0b4 50%, #50575d 100%)',
    'light-wash': 'linear-gradient(135deg, #d5e3ef 0%, #9bb6d0 48%, #6f91b1 100%)',
    denim: 'linear-gradient(135deg, #799bbd 0%, #4e739b 50%, #2d4f75 100%)',
    'medium-wash': 'linear-gradient(135deg, #799bbd 0%, #4e739b 50%, #2d4f75 100%)',
    'dark-wash': 'linear-gradient(135deg, #365a83 0%, #172942 52%, #0d1726 100%)',
    'washed-black': 'linear-gradient(135deg, #111820 0%, #3c444b 48%, #050709 100%)',
    'brown-wash': 'linear-gradient(135deg, #a78b72 0%, #73523f 50%, #3e3028 100%)',
    'deep-blue': 'linear-gradient(135deg, #6f92aa 0%, #315d7e 50%, #18334e 100%)',
    'steel-blue': 'linear-gradient(135deg, #6f92aa 0%, #315d7e 50%, #18334e 100%)',
    phantom: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    ambush: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    eclipse: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    fearless: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    hustler: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    mayhem: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    mystique: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    'no-limits': 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    riot: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    shadow: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    untamed: 'linear-gradient(135deg, #12304b 0%, #315e7e 45%, #091a2c 100%)',
    outlaw: 'linear-gradient(135deg, #d6e4ee 0%, #8fb0c8 48%, #527b9e 100%)',
    breakdown: 'linear-gradient(135deg, #d6e4ee 0%, #8fb0c8 48%, #527b9e 100%)',
    'laid-back': 'linear-gradient(135deg, #d6e4ee 0%, #8fb0c8 48%, #527b9e 100%)',
    lowkey: 'linear-gradient(135deg, #d6e4ee 0%, #8fb0c8 48%, #527b9e 100%)',
    'vintage-light-wash': 'linear-gradient(135deg, #d6e4ee 0%, #8fb0c8 48%, #527b9e 100%)',
    taffy: 'linear-gradient(135deg, #eef4ee 0%, #d9e5df 46%, #9fb7ad 100%)',
    authentic: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    blissful: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    carefree: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    cosmic: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    curveball: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    'free-spirit': 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    'good-vibes': 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    icon: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    iconic: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    legacy: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    legend: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    muse: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    oasis: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    overheard: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    relentless: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    serene: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    stardust: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    thrill: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    treasure: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    unbothered: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    unfazed: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    'urban-drift': 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    wild: 'linear-gradient(135deg, #8ba8c4 0%, #557aa2 48%, #24476b 100%)',
    camo: 'radial-gradient(circle at 22% 28%, #3f4a2f 0 18%, transparent 19%), radial-gradient(circle at 70% 24%, #8f8051 0 16%, transparent 17%), radial-gradient(circle at 46% 74%, #29351f 0 20%, transparent 21%), linear-gradient(135deg, #b3a06b 0%, #6f7650 56%, #3f4a2f 100%)',
    camouflage: 'radial-gradient(circle at 22% 28%, #3f4a2f 0 18%, transparent 19%), radial-gradient(circle at 70% 24%, #8f8051 0 16%, transparent 17%), radial-gradient(circle at 46% 74%, #29351f 0 20%, transparent 21%), linear-gradient(135deg, #b3a06b 0%, #6f7650 56%, #3f4a2f 100%)',
    leopard: 'radial-gradient(circle at 22% 32%, #1f1710 0 8%, transparent 9%), radial-gradient(circle at 68% 28%, #1f1710 0 7%, transparent 8%), radial-gradient(circle at 52% 72%, #1f1710 0 10%, transparent 11%), linear-gradient(135deg, #d5a15d 0%, #9b6034 100%)',
    cheetah: 'radial-gradient(circle at 22% 32%, #1f1710 0 8%, transparent 9%), radial-gradient(circle at 68% 28%, #1f1710 0 7%, transparent 8%), radial-gradient(circle at 52% 72%, #1f1710 0 10%, transparent 11%), linear-gradient(135deg, #d5a15d 0%, #9b6034 100%)',
    'animal-print': 'radial-gradient(circle at 22% 32%, #1f1710 0 8%, transparent 9%), radial-gradient(circle at 68% 28%, #1f1710 0 7%, transparent 8%), radial-gradient(circle at 52% 72%, #1f1710 0 10%, transparent 11%), linear-gradient(135deg, #d5a15d 0%, #9b6034 100%)',
    'cow-print': 'radial-gradient(circle at 28% 32%, #151515 0 18%, transparent 19%), radial-gradient(circle at 74% 66%, #151515 0 22%, transparent 23%), linear-gradient(135deg, #f8f5ee 0%, #ffffff 100%)',
    cowprint: 'radial-gradient(circle at 28% 32%, #151515 0 18%, transparent 19%), radial-gradient(circle at 74% 66%, #151515 0 22%, transparent 23%), linear-gradient(135deg, #f8f5ee 0%, #ffffff 100%)',
    tortoise: 'radial-gradient(circle at 24% 28%, #2b170d 0 12%, transparent 13%), radial-gradient(circle at 66% 34%, #4c2411 0 10%, transparent 11%), radial-gradient(circle at 52% 74%, #2b170d 0 14%, transparent 15%), linear-gradient(135deg, #d7963e 0%, #8a431f 100%)',
    tortoiseshell: 'radial-gradient(circle at 24% 28%, #2b170d 0 12%, transparent 13%), radial-gradient(circle at 66% 34%, #4c2411 0 10%, transparent 11%), radial-gradient(circle at 52% 74%, #2b170d 0 14%, transparent 15%), linear-gradient(135deg, #d7963e 0%, #8a431f 100%)',
    gingham: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.48) 0 4px, transparent 4px 8px), repeating-linear-gradient(90deg, rgba(255,255,255,0.48) 0 4px, transparent 4px 8px), linear-gradient(135deg, #b78355, #d8c4aa)',
    'checkered-black': 'conic-gradient(#111111 25%, #f7f3ea 0 50%, #111111 0 75%, #f7f3ea 0) 0 0 / 10px 10px',
    checkeredblack: 'conic-gradient(#111111 25%, #f7f3ea 0 50%, #111111 0 75%, #f7f3ea 0) 0 0 / 10px 10px',
    'checkered-tan': 'conic-gradient(#b78355 25%, #f3e4cc 0 50%, #b78355 0 75%, #f3e4cc 0) 0 0 / 10px 10px',
    floral: 'radial-gradient(circle at 30% 30%, #e7799d 0 10%, transparent 11%), radial-gradient(circle at 72% 38%, #f2c84b 0 8%, transparent 9%), radial-gradient(circle at 48% 72%, #c13c8a 0 9%, transparent 10%), linear-gradient(135deg, #f7e8ea 0%, #7e9f6c 100%)',
    paisley: 'linear-gradient(135deg, #d5e3ef 0%, #9bb6d0 48%, #6f91b1 100%)'
  };

  const getSwatchStyle = (value) => {
    const key = swatchKey(value);
    const published = getPublishedSwatch(value);
    const themeVector = getThemeVector(key);

    if (themeVector) {
      return `--swatch-color: ${published?.color || swatchColorMap[key] || '#d9d9d9'}; --swatch-image: url(${JSON.stringify(themeVector)});`;
    }

    if (published) {
      return published.image
        ? `--swatch-color: ${published.color}; --swatch-image: ${published.image};`
        : `--swatch-color: ${published.color};`;
    }

    let color = swatchColorMap[key] || '#d9d9d9';
    let image = swatchImageMap[key] || '';

    if (color === '#d9d9d9') {
      if (key.includes('camo') || key.includes('camouflage')) {
        color = '#65704a';
        image = swatchImageMap.camo;
      } else if (key.includes('leopard') || key.includes('cheetah') || key.includes('animal-print')) {
        color = '#b9783f';
        image = swatchImageMap.leopard;
      } else if (key.includes('cow')) {
        color = '#f8f5ee';
        image = swatchImageMap['cow-print'];
      } else if (key.includes('tortoise')) {
        color = '#a55f2c';
        image = swatchImageMap.tortoise;
      } else if (key.includes('gingham')) {
        color = '#b78355';
        image = swatchImageMap.gingham;
      } else if (key.includes('checkered') && key.includes('black')) {
        color = '#111111';
        image = swatchImageMap['checkered-black'];
      } else if (key.includes('checkered')) {
        color = '#b78355';
        image = swatchImageMap['checkered-tan'];
      } else if (key.includes('floral')) {
        color = '#e7799d';
        image = swatchImageMap.floral;
      } else if (key.includes('paisley')) {
        color = '#a8bfd5';
        image = swatchImageMap.paisley;
      } else if (key.includes('rainbow')) {
        color = '#e85c82';
        image = swatchImageMap.rainbow;
      } else if (key.includes('ombre') && key.includes('violet')) {
        color = '#8554bd';
        image = swatchImageMap['ombre-violet'];
      } else if (key.includes('multi')) {
        image = swatchImageMap.multi;
      } else if (key.includes('black') && key.includes('wash')) {
        color = '#1f2428';
        image = swatchImageMap['black-wash'];
      } else if (key.includes('dark') && key.includes('wash')) {
        color = '#243b5b';
        image = swatchImageMap['dark-wash'];
      } else if ((key.includes('grey') || key.includes('gray')) && key.includes('wash')) {
        color = '#80878c';
        image = swatchImageMap['grey-wash'];
      } else if (key.includes('light') && key.includes('wash')) {
        color = '#a8bfd5';
        image = swatchImageMap['light-wash'];
      } else if (key.includes('medium') && key.includes('wash')) {
        color = '#557aa2';
        image = swatchImageMap['medium-wash'];
      } else if (key.includes('black')) color = '#111111';
      else if (key.includes('white')) color = '#ffffff';
      else if (key.includes('cream') || key.includes('ivory')) color = '#f4ecd8';
      else if (key.includes('nude')) color = '#d9b8a3';
      else if (key.includes('natural') || key.includes('bone') || key.includes('ballet')) color = '#d8c4aa';
      else if (key.includes('cinnamon')) color = '#9a4f2f';
      else if (key.includes('toast')) color = '#b9875b';
      else if (key.includes('brown') || key.includes('mocha') || key.includes('espresso') || key.includes('coffee') || key.includes('caramel') || key.includes('cognac') || key.includes('camel') || key.includes('mahogany')) color = '#6f4e37';
      else if (key.includes('taupe')) color = '#8b7c6e';
      else if (key.includes('pink') || key.includes('blush') || key.includes('rose') || key.includes('blossom') || key.includes('flamingo') || key.includes('bubblegum') || key.includes('strawberry') || key.includes('watermelon') || key.includes('hibiscus')) color = '#e8a5b5';
      else if (key.includes('blue') || key.includes('cobalt') || key.includes('coastal')) color = '#3269b1';
      else if (key.includes('navy') || key.includes('midnight')) color = '#18253f';
      else if (key.includes('green') || key.includes('olive') || key.includes('sage') || key.includes('mint') || key.includes('pistachio') || key.includes('fern') || key.includes('spruce') || key.includes('lime')) color = '#4f7a55';
      else if (key.includes('teal')) color = '#277c78';
      else if (key.includes('cranberry')) color = '#9f1f3a';
      else if (key.includes('tomato')) color = '#d8452f';
      else if (key.includes('red') || key.includes('cherry') || key.includes('poppy')) color = '#b3261e';
      else if (key.includes('pale') && key.includes('mauve')) color = '#dec7d2';
      else if (key.includes('purple') || key.includes('lavender') || key.includes('lilac') || key.includes('violet') || key.includes('mauve') || key.includes('boysenberry')) color = '#7759a6';
      else if (key.includes('yellow') || key.includes('gold') || key.includes('lemon') || key.includes('limoncello') || key.includes('butter')) color = '#e5c64a';
      else if (key.includes('soft') && key.includes('peach')) color = '#f2d8d3';
      else if (key.includes('terracotta')) color = '#b85f45';
      else if (key.includes('orange') || key.includes('coral') || key.includes('tangerine') || key.includes('peach') || key.includes('apricot') || key.includes('papaya') || key.includes('amber')) color = '#d9793d';
      else if (key.includes('gray') || key.includes('grey')) color = '#9a9a9a';
    }

    return image ? `--swatch-color: ${color}; --swatch-image: ${image};` : `--swatch-color: ${color};`;
  };

  const getUniqueSwatches = (swatches) => {
    const seenColors = new Set();

    return (Array.isArray(swatches) ? swatches : []).filter((swatch) => {
      if (!swatch || swatch.hidden || swatch.hideVariant) return false;

      const color = String(swatch.color || '').trim();
      const colorKey = color.toLocaleLowerCase().replace(/\s+/g, ' ');

      if (!colorKey || seenColors.has(colorKey)) return false;

      seenColors.add(colorKey);
      return true;
    });
  };

  const refresh = (root = document) => {
    root.querySelectorAll?.('[data-swatch-value]').forEach((element) => {
      const style = getSwatchStyle(element.dataset.swatchValue || '');
      const color = style.match(/--swatch-color:\s*([^;]+);?/i)?.[1]?.trim() || '#d9d9d9';
      const image = style.match(/--swatch-image:\s*(.+);$/i)?.[1]?.trim() || '';

      element.style.setProperty('--swatch-color', color);
      if (image) element.style.setProperty('--swatch-image', image);
      else element.style.removeProperty('--swatch-image');
      element.style.removeProperty('--swatch-visibility');
      element.style.removeProperty('--swatch-background-size');
      element.style.removeProperty('--swatch-loading-animation');
      element.style.removeProperty('--swatch-worker-pending');
    });

    root.querySelectorAll?.(
      '[data-product-card-selected-color-text], [data-selected-option-value]'
    ).forEach((element) => {
      const rawLabel = element.dataset.swatchLabelRaw || element.textContent || '';
      if (!rawLabel.trim()) return;

      element.dataset.swatchLabelRaw = rawLabel;
      element.textContent = getSwatchLabel(rawLabel);
    });
  };

  const loadPublishedSwatches = async () => {
    const endpoint = String(config.endpoint || '').trim();
    if (config.enabled === false || !endpoint) {
      refresh();
      return publishedSwatches;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(endpoint, {
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) return publishedSwatches;

      const payload = await response.json();
      if (!payload?.swatches || typeof payload.swatches !== 'object') return publishedSwatches;

      Object.entries(payload.swatches).forEach(([handle, entry]) => {
        const normalized = normalizePublishedSwatch(entry);
        if (normalized) publishedSwatches.set(handleize(handle), normalized);
      });
      refresh();
      document.dispatchEvent(new CustomEvent('wob:swatches-ready', {
        detail: { count: publishedSwatches.size }
      }));
    } catch {
      // Existing theme swatches remain the storefront-safe fallback.
    } finally {
      window.clearTimeout(timeoutId);
      refresh();
    }

    return publishedSwatches;
  };

  window.WobSwatches = {
    getSwatchStyle,
    getSwatchLabel,
    getUniqueSwatches,
    refresh,
    ready: loadPublishedSwatches()
  };
})();
